import React from 'react';
import { format } from 'date-fns';
import { openUserProfile } from 'features/users/openUserProfile';
import AgentMarkdownContent from 'features/agent-analytics/components/AgentMarkdownContent';
import {
  LinkifiedPre, PayloadEntityValue,
} from 'features/agent-analytics/components/payloadEntityLinks';
import AnnotationBadge from 'features/agent-analytics/annotations/AnnotationBadge';
import {
  tableHeaderRowClass,
  tableHeaderCellClass,
  tableDataCellClass,
} from 'features/activity/styles/dataTableStyles';

/** Crisp inline thumb icons — outline by default, solid when `filled`. Colour
 *  is driven by `color` (defaults to the semantic feedback tokens) so they sit
 *  on-brand anywhere. */
export function ThumbUpIcon({ size = 14, color = 'var(--feedback-success)', filled = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ color }} aria-hidden="true">
      <path d="M7 10v12" />
      <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
    </svg>
  );
}
export function ThumbDownIcon({ size = 14, color = 'var(--feedback-error)', filled = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ color }} aria-hidden="true">
      <path d="M17 14V2" />
      <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
    </svg>
  );
}

export function DistinctIdLink({ value }) {
  if (value == null || value === '') return <span className="text-[color:var(--text-muted)]">—</span>;
  const label = String(value);
  const truncated = label.length > 32 ? `${label.slice(0, 32)}…` : label;
  // Opens the user drawer in place, on any page and in the chat alike.
  // stopPropagation: clicking the USER cell means the user, not the row's run.
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); openUserProfile(label); }}
      title={label}
      className="cursor-pointer border-0 bg-transparent p-0 text-left font-mono text-[11px] text-[color:var(--text-primary)] underline-offset-2 hover:underline"
    >
      {truncated}
    </button>
  );
}

export function formatDuration(ms) {
  if (ms == null) return '—';
  const n = Number(ms);
  if (!Number.isFinite(n)) return '—';
  if (n < 1000) return `${Math.round(n)} ms`;
  const s = n / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s - m * 60);
  return `${m}m ${rem}s`;
}

/**
 * Wall-clock span, for durations that can run to hours or days.
 *
 * formatDuration above stops at minutes, which is right for a run (milliseconds
 * to minutes) but turns a two-day conversation into "3293m 19s".
 */
export function formatElapsed(ms) {
  if (ms == null) return '—';
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return '—';
  const s = Math.round(n / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

export function formatNumber(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString();
}

export function formatCost(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '$0';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

export function formatDate(iso) {
  if (!iso) return '—';
  try { return format(new Date(iso), 'yyyy-MM-dd HH:mm:ss'); }
  catch { return iso; }
}

/** Compact "how long ago" — e.g. 5s, 12min, 3h, 2d, 1w, 4mo, 1y. */
export function formatRelativeTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  if (day < 30) return `${Math.floor(day / 7)}w ago`;
  if (day < 365) return `${Math.floor(day / 30)}mo ago`;
  return `${Math.floor(day / 365)}y ago`;
}

/** Small status dot: green = ok, red = error, blue = running. */
export function StatusDot({ status }) {
  const color =
    status === 'error'   ? 'var(--feedback-error)' :
    status === 'running' ? 'var(--feedback-info)' :
    status === 'ok'      ? 'var(--feedback-success)' :
                           'var(--text-disabled)';
  return (
    <span
      className="inline-block h-2 w-2 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
      title={status || 'unknown'}
      aria-label={status || 'unknown'}
    />
  );
}

/** Severity chip (Langfuse-style). Renders nothing for the neutral 'default'
 *  level so healthy spans stay clean — only warning/error/debug get a chip.
 *  Pass `status` (or `status="error"` inside an error box): a level that only
 *  repeats it — ERROR beside "Error" — is noise, so it is not drawn. */
export function LevelBadge({ level, status }) {
  if (!level || level === 'default' || level === 'ok') return null;
  if (status && String(status).toLowerCase() === String(level).toLowerCase()) return null;
  const style = {
    error:   { bg: 'var(--feedback-error-bg)', fg: 'var(--feedback-error)' },
    warning: { bg: 'var(--surface-hover)',     fg: 'var(--feedback-warning)' },
    debug:   { bg: 'var(--surface-hover)',     fg: 'var(--text-muted)' },
  }[level] || { bg: 'var(--surface-hover)', fg: 'var(--text-secondary)' };
  return (
    <span
      className="inline-flex items-center rounded px-1.5 py-px text-[9px] font-bold uppercase tracking-wide"
      style={{ backgroundColor: style.bg, color: style.fg }}
      title={`Severity: ${level}`}
    >
      {level}
    </span>
  );
}

/** Monospace HTTP/provider status-code chip (e.g. 429, rate_limit_exceeded). */
export function StatusCodePill({ code }) {
  if (code == null || code === '') return null;
  return (
    <span
      className="inline-flex items-center rounded px-1.5 py-px font-mono text-[10px]"
      style={{ backgroundColor: 'var(--surface-hover)', color: 'var(--text-secondary)' }}
      title={`Status code: ${code}`}
    >
      {code}
    </span>
  );
}

export function truncate(str, max = 32) {
  if (str == null) return '—';
  const s = String(str);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** Convert a 0..1 score to an integer percent. Accepts already-percent values
 *  too (e.g. rolled-up `rage_pct` from the grouped endpoint). Returns null when
 *  the input is missing/non-numeric. */
export function scoreToPct(value) {
  if (value == null) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n <= 1 ? Math.round(n * 100) : Math.round(n);
}

export function formatScoreAsPct(value) {
  const pct = scoreToPct(value);
  return pct == null ? '—' : `${pct}%`;
}

export function StatusPill({ status }) {
  const style = {
    ok:      { bg: 'var(--feedback-success-bg)', fg: 'var(--feedback-success)', label: 'OK' },
    error:   { bg: 'var(--feedback-error-bg)', fg: 'var(--feedback-error)', label: 'Error' },
    running: { bg: 'var(--feedback-info-bg)', fg: 'var(--feedback-info)', label: 'Running' },
  }[status] || { bg: 'var(--surface-hover)', fg: 'var(--text-secondary)', label: status || '—' };
  return (
    <span
      className="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium"
      style={{ backgroundColor: style.bg, color: style.fg }}
    >
      {style.label}
    </span>
  );
}

export function FeedbackCell({ row }) {
  const sat = row.feedback_satisfaction;
  const rating = row.feedback_rating;
  if (!sat && rating == null) return <span className="text-[color:var(--text-muted)]">—</span>;
  return (
    <span className="inline-flex items-center gap-1">
      {sat === 'positive' ? <ThumbUpIcon /> : sat === 'negative' ? <ThumbDownIcon /> : null}
      {rating != null ? <span className="tabular-nums">{Number(rating).toFixed(1)}</span> : null}
    </span>
  );
}

/** Pill for rage% / sat%. `kind` controls thresholding:
 *   - 'rage'  → red when ≥40%, neutral otherwise
 *   - 'sat'   → green when ≥70%, red when ≤30%, neutral in between
 */
export function ScorePill({ value, kind }) {
  const pct = scoreToPct(value);
  if (pct == null) return <span className="text-[color:var(--text-muted)]">—</span>;
  // Inline value — no container. Colour alone reveals severity.
  let color = 'var(--text-primary)';
  if (kind === 'rage') {
    if (pct >= 60) color = 'var(--feedback-error)';
    else if (pct >= 40) color = 'var(--feedback-warning)';   // amber: rising rage
  } else if (kind === 'sat') {
    if (pct <= 30) color = 'var(--feedback-error)';
    else if (pct >= 70) color = 'var(--feedback-success)';   // green: healthy satisfaction
    else color = 'var(--feedback-warning)';                   // amber: middling
  }
  return (
    <span
      className="tabular-nums font-medium"
      style={{ color }}
      title={`${kind === 'rage' ? 'Rage' : 'Satisfaction'} ${pct}%`}
    >
      {pct}%
    </span>
  );
}

/** Compact tag with a quiet label and a strong value. Optional `tone` paints
 *  the value chip red/green/amber for status-like signals; default is grey. */
export function Tag({ label, value, mono, tone, title }) {
  const valueColor =
    tone === 'red'   ? 'var(--feedback-error)' :
    tone === 'green' ? 'var(--feedback-success)' :
    tone === 'amber' ? 'var(--feedback-warning)' :
                       'var(--text-primary)';
  return (
    <span
      title={title}
      className="inline-flex flex-col"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      <span className="text-[10px] text-[color:var(--text-muted)] leading-tight">{label}</span>
      <span className={`text-[12px] font-semibold leading-tight ${mono ? 'font-mono' : ''}`} style={{ color: valueColor }}>
        {value}
      </span>
    </span>
  );
}

// A string is "rich" when it carries structure worth rendering as markdown —
// headings, bullet/numbered lists, tables, bold, code fences, blockquotes — or
// is simply long/multi-line prose. These render through the markdown pipeline
// (tables, lists, bold, titles) instead of as a flat plain-text blob.
function looksLikeMarkdown(s) {
  return (
    /^#{1,6}\s/m.test(s)         // # headings
    || /^\s*[-*+]\s+\S/m.test(s) // - bullet lists
    || /^\s*\d+\.\s+\S/m.test(s) // 1. numbered lists
    || /\*\*[^*\n]+\*\*/.test(s) // **bold**
    || /(^|\n)\s*\|.+\|\s*(\n|$)/.test(s) // | table | rows
    || /```/.test(s)             // ``` code fences
    || /^>\s+\S/m.test(s)        // > blockquotes
  );
}
function isRichText(v) {
  return typeof v === 'string' && (v.includes('\n') || v.length > 200 || looksLikeMarkdown(v));
}

/** Serialize any value to text for the clipboard — strings verbatim, everything
 *  else as pretty JSON. */
export function stringifyForCopy(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') { try { return JSON.stringify(v, null, 2); } catch { return String(v); } }
  return String(v);
}

function CopyGlyph({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
function CheckGlyph({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/** Copy-to-clipboard icon button. `value` is copied verbatim (string) or as
 *  pretty JSON (object). It's hidden until its OWN title line is hovered — the
 *  `group-hover/row` scope means only the pointed-at line shows its icon, not
 *  every ancestor. Place inside a `group/row` line, pushed right with ml-auto. */
export function CopyButton({ value, label = 'Copy', className = '' }) {
  const [copied, setCopied] = React.useState(false);
  const onClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const text = stringifyForCopy(value);
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1200); };
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else { done(); }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`ml-auto inline-flex flex-none items-center justify-center rounded p-0.5 text-[color:var(--text-muted)] opacity-0 transition-opacity hover:bg-[color:var(--surface-hover)] hover:text-[color:var(--text-primary)] focus:opacity-100 group-hover/row:opacity-100 ${className}`}
    >
      {copied ? <CheckGlyph /> : <CopyGlyph />}
    </button>
  );
}

// A one-line, whitespace-collapsed text preview of any value (for collapsed rows).
function textPreview(v, n = 140) {
  const s = typeof v === 'string' ? v : stringifyForCopy(v);
  const flat = s.replace(/\s+/g, ' ').trim();
  return flat.length > n ? `${flat.slice(0, n)}…` : flat;
}

/** A collapsible titled section: a chevron + bright title + hover copy icon on
 *  one line, and the formatted content below it when open. Collapsed shows a
 *  muted one-line preview. Used for chat turns so long messages fold away. */
function Collapsible({ title, mono = false, copyValue, preview, defaultOpen = true, children }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="group/row flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex-none rounded text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
          aria-label={open ? 'Collapse' : 'Expand'}
          title={open ? 'Collapse' : 'Expand'}
        >
          <Chevron open={open} />
        </button>
        <span className={`text-[12px] font-semibold text-[color:var(--text-primary)] ${mono ? 'font-mono' : 'uppercase tracking-wide'}`}>
          {title}
        </span>
        <CopyButton value={copyValue} label={`Copy ${title}`} />
      </div>
      {open ? (
        <div className="min-w-0 pl-[18px]">{children}</div>
      ) : (preview ? (
        <button type="button" onClick={() => setOpen(true)} className="min-w-0 truncate pl-[18px] text-left text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-secondary)]">
          {preview}
        </button>
      ) : null)}
    </div>
  );
}

// Ordered union of keys across an array of row objects (first-seen order).
function tableColumns(rows) {
  const seen = [];
  const set = new Set();
  rows.forEach((r) => Object.keys(r).forEach((k) => {
    if (!set.has(k)) { set.add(k); seen.push(k); }
  }));
  return seen;
}

/** A value reads better as a TABLE when it's an array of ≥2 similar flat records
 *  — plain objects, a bounded column count, and no oversized cell (a long/rich
 *  string or a big nested structure). Those exceptions render as stacked blocks
 *  instead, since squeezing them into a cell is unreadable. */
function looksTabular(v) {
  if (!Array.isArray(v) || v.length < 2) return false;
  if (!v.every((r) => r && typeof r === 'object' && !Array.isArray(r))) return false;
  const cols = tableColumns(v);
  if (cols.length === 0 || cols.length > 15) return false;
  for (const r of v) {
    for (const k of cols) {
      const cell = r[k];
      if (typeof cell === 'string' && (cell.length > 160 || cell.includes('\n'))) return false;
      if (cell && typeof cell === 'object') {
        const size = Array.isArray(cell) ? cell.length : Object.keys(cell).length;
        if (size > 8) return false;
      }
    }
  }
  return true;
}

// Row-array keys a table envelope might carry, in priority order.
const ROW_ARRAY_KEYS = ['rows', 'sample', 'data', 'items', 'records'];
// Sibling meta keys that don't disqualify a pure table envelope.
const TABLE_META_KEYS = new Set(['cols', 'columns', 'numeric', 'schema', 'total', 'count']);

/** A query-result / digest envelope wrapping a row array — e.g.
 *  { rows, _total, _truncated } or { _digest, _rows, cols, numeric, sample }.
 *  Detected when a known row key holds a tabular array and every OTHER key is
 *  underscore-meta or a recognized table-meta key. Returns the rows plus column
 *  order, per-column numeric stats, total, and whether it was truncated. */
function asTableEnvelope(v) {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const rowKey = ROW_ARRAY_KEYS.find((k) => looksTabular(v[k]));
  if (!rowKey) return null;
  const others = Object.keys(v).filter((k) => k !== rowKey);
  const clean = v._digest === true || others.every((k) => k.startsWith('_') || TABLE_META_KEYS.has(k));
  if (!clean) return null;
  const rows = v[rowKey];
  const total = v._total != null ? v._total : (v._rows != null ? v._rows : v.total);
  const truncated = v._truncated != null ? v._truncated : (total != null && rows.length < Number(total));
  const cols = (Array.isArray(v.cols) && v.cols.length ? v.cols
    : (Array.isArray(v.columns) && v.columns.length ? v.columns : null));
  const numeric = (v.numeric && typeof v.numeric === 'object' && !Array.isArray(v.numeric)) ? v.numeric : null;
  return { rows, total, truncated, cols, numeric };
}

// Compact numeric formatting for the per-column digest stats.
function fmtStat(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return String(n);
  if (Math.abs(v) >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return String(Math.round(v * 1000) / 1000);
}
function NumericSummary({ numeric }) {
  const cols = Object.keys(numeric || {});
  if (!cols.length) return null;
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      {cols.map((c) => {
        const s = numeric[c] || {};
        return (
          <div key={c} className="flex flex-wrap items-baseline gap-x-2 text-[10px] text-[color:var(--text-muted)]">
            <span className="font-mono text-[color:var(--text-secondary)]">{c}</span>
            {s.mean != null ? <span>mean {fmtStat(s.mean)}</span> : null}
            {s.min != null ? <span>min {fmtStat(s.min)}</span> : null}
            {s.max != null ? <span>max {fmtStat(s.max)}</span> : null}
            {s.sum != null ? <span>sum {fmtStat(s.sum)}</span> : null}
          </div>
        );
      })}
    </div>
  );
}

function TableCell({ cell }) {
  if (cell == null) return <span className="text-[color:var(--text-muted)]">—</span>;
  if (typeof cell === 'object') {
    let text;
    try { text = JSON.stringify(cell); } catch { text = String(cell); }
    return <span className="font-mono text-[10px] text-[color:var(--text-secondary)]">{text}</span>;
  }
  return <span>{String(cell)}</span>;
}

/** Auto-detected data table. The wrapper scrolls horizontally on its own so a
 *  wide table never pushes the page sideways; a caption notes row/total counts. */
export function DataTable({ rows, cols, numeric, total, truncated }) {
  const columns = (cols && cols.length) ? cols : tableColumns(rows);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="min-w-0 max-w-full overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-left">
            <thead>
              <tr className={tableHeaderRowClass}>
                {columns.map((c) => (
                  <th key={c} className={tableHeaderCellClass}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="h-8 border-b border-[color:var(--border-subtle)] last:border-b-0">
                  {columns.map((c) => (
                    <td key={c} className={`${tableDataCellClass} whitespace-nowrap tabular-nums`}>
                      <TableCell cell={r[c]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {(total != null || truncated) ? (
        <span className="text-[10px] text-[color:var(--text-muted)]">
          Showing {rows.length}{total != null ? ` of ${total}` : ''}{truncated ? ' · truncated' : ''}
        </span>
      ) : null}
      {numeric ? <NumericSummary numeric={numeric} /> : null}
    </div>
  );
}

function Chevron({ open }) {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 120ms' }}>
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

// Compact preview of a collapsed array's elements, e.g. [{"type": …}, …].
function previewElem(x) {
  if (x == null) return String(x);
  if (Array.isArray(x)) return x.length ? '[…]' : '[]';
  if (typeof x === 'object') { const k = Object.keys(x)[0]; return k ? `{"${k}": …}` : '{}'; }
  if (typeof x === 'string') return `"${x.length > 14 ? `${x.slice(0, 14)}…` : x}"`;
  return String(x);
}
function arrayPreview(arr) {
  if (!arr.length) return '[]';
  const parts = arr.slice(0, 3).map(previewElem);
  return `[${parts.join(', ')}${arr.length > 3 ? ', …' : ''}]`;
}

// The Value column for one tree node: a summary for expandable nodes, the value
// itself for leaves (short strings keep entity links; long/markdown preview).
function TreeValue({ val, entityProps }) {
  if (val === null || val === undefined) return <span className="text-[color:var(--text-muted)]">{String(val)}</span>;
  if (Array.isArray(val)) return <span className="font-mono text-[color:var(--text-muted)]">{arrayPreview(val)}</span>;
  if (typeof val === 'object') { const n = Object.keys(val).length; return <span className="text-[color:var(--text-muted)]">{n} item{n === 1 ? '' : 's'}</span>; }
  if (typeof val === 'string') {
    if (isRichText(val)) {
      const preview = val.length > 140 ? `${val.slice(0, 140)}…` : val;
      return <span className="whitespace-pre-wrap break-words text-[color:var(--text-secondary)]">{preview}</span>;
    }
    return <PayloadEntityValue value={val} {...entityProps} />;
  }
  return <span className="font-mono text-[color:var(--text-primary)]">{String(val)}</span>;
}

const TREE_COLS = 'grid grid-cols-[minmax(110px,34%)_1fr]';

function TreeNodeRow({ row, toggle, entityProps }) {
  const { path, label, depth, val, expandable, isOpen } = row;
  return (
    <div className={`group/row ${TREE_COLS} border-b border-[color:var(--border-subtle)] last:border-0`}>
      <div className="flex min-w-0 items-start gap-1 py-1.5 pr-2" style={{ paddingLeft: 8 + depth * 14 }}>
        {expandable ? (
          <button type="button" onClick={() => toggle(path)} className="mt-0.5 flex-none rounded text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]" aria-label={isOpen ? 'Collapse' : 'Expand'} title={isOpen ? 'Collapse' : 'Expand'}>
            <Chevron open={isOpen} />
          </button>
        ) : <span className="mt-0.5 w-[11px] flex-none" />}
        <span className={`min-w-0 break-words font-mono text-[11px] ${expandable ? 'font-semibold text-[color:var(--text-primary)]' : 'font-medium text-[color:var(--text-secondary)]'}`}>{label}</span>
        <CopyButton value={val} label={`Copy ${label}`} />
      </div>
      <div className="min-w-0 break-words py-1.5 pr-2.5 text-[11px] leading-relaxed">
        <TreeValue val={val} entityProps={entityProps} />
      </div>
    </div>
  );
}

/** Collapsible Path / Value tree (Langfuse-style) for structured JSON — objects
 *  and arrays render as indented, expandable rows so deeply-nested payloads stay
 *  scannable. Chat arrays expand to titled turns, clean record sets expand to a
 *  data table, and long/markdown strings expand to rendered markdown. Each row
 *  has a hover copy icon for its own value. */
export function JsonTree({ value, onRunClick, onSpanClick }) {
  const entityProps = { onRunClick, onSpanClick };
  const [open, setOpen] = React.useState(() => {
    const s = new Set();
    if (value && typeof value === 'object') {
      const keys = Array.isArray(value) ? value.map((_, i) => String(i)) : Object.keys(value);
      keys.forEach((k) => s.add(`$.${k}`)); // top level expanded by default
    }
    return s;
  });
  const toggle = (p) => setOpen((s) => { const n = new Set(s); if (n.has(p)) n.delete(p); else n.add(p); return n; });

  // A scalar/markdown value isn't a tree — defer to the leaf renderer.
  if (!value || typeof value !== 'object') return <JsonView value={value} {...entityProps} />;

  const rows = [];
  const walk = (val, label, path, depth) => {
    const arr = Array.isArray(val);
    const obj = val && typeof val === 'object' && !arr;
    const richStr = typeof val === 'string' && isRichText(val);
    const expandable = arr || obj || richStr;
    const isOpen = open.has(path);
    rows.push({ kind: 'node', path, label, depth, val, expandable, isOpen });
    if (!expandable || !isOpen) return;
    if (arr && isMessageArray(val)) {
      rows.push({ kind: 'block', path: `${path}#b`, depth: depth + 1, node: <MessagesView messages={val} {...entityProps} /> });
    } else if (arr && looksTabular(val)) {
      rows.push({ kind: 'block', path: `${path}#b`, depth: depth + 1, node: <DataTable rows={val} /> });
    } else if (obj && asTableEnvelope(val)) {
      const env = asTableEnvelope(val);
      rows.push({ kind: 'block', path: `${path}#b`, depth: depth + 1, node: <DataTable rows={env.rows} cols={env.cols} numeric={env.numeric} total={env.total} truncated={env.truncated} /> });
    } else if (obj) {
      Object.entries(val).forEach(([k, v]) => walk(v, k, `${path}.${k}`, depth + 1));
    } else if (arr) {
      val.forEach((v, i) => walk(v, String(i), `${path}.${i}`, depth + 1));
    } else {
      // rich/long string → rendered markdown on expand
      rows.push({ kind: 'block', path: `${path}#b`, depth: depth + 1, node: <AgentMarkdownContent {...entityProps}>{val}</AgentMarkdownContent> });
    }
  };
  (Array.isArray(value) ? value.map((v, i) => [String(i), v]) : Object.entries(value))
    .forEach(([k, v]) => walk(v, k, `$.${k}`, 0));

  return (
    <div className="min-w-0 overflow-hidden border-t border-[color:var(--border-subtle)]">
      {rows.map((r, i) => (r.kind === 'block' ? (
        <div key={i} className="min-w-0 border-b border-[color:var(--border-subtle)] last:border-0" style={{ paddingLeft: 8 + r.depth * 14 }}>
          <div className="min-w-0 py-2 pr-2.5">{r.node}</div>
        </div>
      ) : (
        <TreeNodeRow key={i} row={r} toggle={toggle} entityProps={entityProps} />
      )))}
    </div>
  );
}

/** Recursive container renderer for parsed JSON. Scalars/markdown render inline;
 *  chat arrays as role-titled turns; clean record sets as a scrolling data table;
 *  any other object/array as a collapsible Path/Value tree (see JsonTree). */
export function JsonView({ value, jsonKey, onRunClick, onSpanClick, depth = 0 }) {
  const entityProps = { onRunClick, onSpanClick };
  if (value === null || value === undefined) return <span className="text-[color:var(--text-muted)]">—</span>;
  // Rich strings (markdown, tables, long prose) render through the markdown
  // pipeline so titles/bullets/tables/bold show properly, not as a flat blob.
  if (isRichText(value)) {
    return (
      <AgentMarkdownContent onRunClick={onRunClick} onSpanClick={onSpanClick}>
        {value}
      </AgentMarkdownContent>
    );
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return (
      <PayloadEntityValue
        value={value}
        jsonKey={jsonKey}
        onRunClick={onRunClick}
        onSpanClick={onSpanClick}
      />
    );
  }
  // Arrays — a chat transcript (nested anywhere, e.g. a `context` array) renders
  // as role-titled turns; a set of similar records auto-renders as a scrolling
  // table; any other array → collapsible Path/Value tree.
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-[color:var(--text-muted)]">[]</span>;
    if (isMessageArray(value)) {
      return <MessagesView messages={value} {...entityProps} />;
    }
    if (looksTabular(value)) {
      return <DataTable rows={value} />;
    }
    return <JsonTree value={value} {...entityProps} />;
  }
  // Query-result / digest envelope ({ rows … } or { _digest, sample, cols,
  // numeric … }) → one self-contained, horizontally-scrolling table.
  const envelope = asTableEnvelope(value);
  if (envelope) {
    return (
      <DataTable
        rows={envelope.rows}
        cols={envelope.cols}
        numeric={envelope.numeric}
        total={envelope.total}
        truncated={envelope.truncated}
      />
    );
  }
  // Any other object → collapsible Path/Value tree (see JsonTree).
  if (Object.keys(value).length === 0) return <span className="text-[color:var(--text-muted)]">{'{}'}</span>;
  return <JsonTree value={value} {...entityProps} />;
}

// AI agents commonly wrap their reply in a single-field envelope
// (e.g. { finalAnswer: "## Summary…" }). Render the inner string as the
// primary payload rather than a one-row key/value table.
const PRIMARY_TEXT_KEYS = [
  'finalAnswer', 'final_answer',
  'output', 'text', 'message', 'answer', 'content', 'response', 'reply',
];

function unwrapPrimaryText(parsed) {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  for (const k of PRIMARY_TEXT_KEYS) {
    if (k in parsed && typeof parsed[k] === 'string' && parsed[k].trim()) {
      return parsed[k];
    }
  }
  return null;
}

/** Best-effort parse of a TRUNCATED JSON string — e.g. the 2000-char span
 *  preview from listForRun, where a chat-message array gets cut mid-element
 *  and plain JSON.parse throws. Walk back to the last complete `}` boundary
 *  and close the container so the intact prefix still renders as titled
 *  blocks instead of a flat text blob. Returns null when nothing salvages. */
function parseTruncatedJson(trimmed) {
  const close = trimmed[0] === '[' ? ']' : '}';
  let end = trimmed.length;
  for (let i = 0; i < 8; i += 1) {
    const cut = trimmed.lastIndexOf('}', end - 1);
    if (cut <= 0) return null;
    try {
      const parsed = JSON.parse(trimmed.slice(0, cut + 1) + close);
      if (parsed && typeof parsed === 'object') return parsed;
    } catch { /* boundary inside a string: walk further back */ }
    end = cut;
  }
  return null;
}

/** Last resort for a chat array cut inside its first message (a long system
 *  prompt fills the whole 2000-char preview): pull out each role and as much
 *  of its content as arrived, so the preview still reads as turns. */
function salvageMessages(trimmed) {
  if (trimmed[0] !== '[' || !/"role"\s*:/.test(trimmed)) return null;
  const out = [];
  const re = /\{\s*"role"\s*:\s*"([^"]+)"\s*,\s*"content"\s*:\s*"((?:[^"\\]|\\.)*)("?)/g;
  let m;
  while ((m = re.exec(trimmed))) {
    let content = m[2];
    try { content = JSON.parse(`"${content.replace(/\\$/, '')}"`); } catch { content = content.replace(/\\n/g, '\n'); }
    out.push({ role: m[1], content: m[3] ? content : `${content}…` });
  }
  return out.length ? out : null;
}

/** Try to parse a value as JSON. Returns { kind: 'json', parsed } or
 *  { kind: 'text', text } or { kind: 'empty' }. Unwraps single-field
 *  AI-agent wrappers so chat-style outputs render as markdown, not tables. */
function classify(value) {
  if (value == null) return { kind: 'empty' };
  if (typeof value === 'object') {
    const inner = unwrapPrimaryText(value);
    if (inner != null) return { kind: 'text', text: inner };
    return { kind: 'json', parsed: value };
  }
  const text = String(value);
  const trimmed = text.trim();
  if (!trimmed) return { kind: 'empty' };
  if (trimmed[0] === '{' || trimmed[0] === '[') {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed !== null && typeof parsed === 'object') {
        const inner = unwrapPrimaryText(parsed);
        if (inner != null) return { kind: 'text', text: inner };
        return { kind: 'json', parsed };
      }
    } catch {
      // Long JSON-looking strings are usually truncated previews (the trace
      // tree caps payloads at 2000 chars) — salvage the intact prefix so
      // message arrays keep rendering as blocks while the full span loads.
      if (trimmed.length >= 500) {
        const parsed = parseTruncatedJson(trimmed) || salvageMessages(trimmed);
        if (parsed) return { kind: 'json', parsed };
      }
    }
  }
  return { kind: 'text', text };
}

/** One-line preview text for a raw input/output payload string. Serialized
 *  chat-message arrays (often truncated by the server's LEFT(…, 240) preview)
 *  flatten to their content text so search previews read as words, not
 *  `[{"role":"system",…` noise. Non-message payloads pass through unchanged. */
export function payloadPreviewText(raw) {
  if (typeof raw !== 'string') return raw;
  const trimmed = raw.trim();
  if (trimmed[0] !== '[' && trimmed[0] !== '{') return raw;
  let parsed = null;
  try { parsed = JSON.parse(trimmed); } catch { parsed = parseTruncatedJson(trimmed); }
  if (!parsed) return raw;
  const arr = asMessageArray(parsed);
  if (!arr) return raw;
  const parts = [];
  for (const m of arr) {
    let c = '';
    if (typeof m.content === 'string') c = m.content;
    else if (m.content != null) { try { c = JSON.stringify(m.content); } catch { c = ''; } }
    if (c) parts.push(c);
  }
  return parts.length ? parts.join(' … ') : raw;
}

/** Pretty-print any value as raw JSON. For string inputs that aren't JSON,
 *  the original string is shown inside the <pre> as-is. */
export function JsonRawView({ value, onRunClick, onSpanClick }) {
  if (value == null) return <span className="text-[12px] text-[color:var(--text-muted)]">—</span>;
  let text;
  if (typeof value === 'object') {
    try { text = JSON.stringify(value, null, 2); } catch { text = String(value); }
  } else {
    const s = String(value);
    const trimmed = s.trim();
    if ((trimmed[0] === '{' || trimmed[0] === '[')) {
      try { text = JSON.stringify(JSON.parse(trimmed), null, 2); }
      catch { text = s; }
    } else {
      text = s;
    }
  }
  return <LinkifiedPre text={text} onRunClick={onRunClick} onSpanClick={onSpanClick} />;
}

// Pretty role titles for chat messages (system / user / assistant / tool /
// context / …). `context` is the Trodo extension role for RAG docs.
const ROLE_LABEL = {
  system: 'System', developer: 'Developer', user: 'User',
  assistant: 'Assistant', tool: 'Tool', function: 'Function', model: 'Model',
  context: 'Context',
};

/** A chat transcript: a non-empty array whose every element is a `{role, …}`
 *  object. Used to render each turn as a titled block (System / User / …) at
 *  any nesting depth — e.g. a `context` array buried inside the input object. */
function isMessageArray(v) {
  return (
    Array.isArray(v) && v.length > 0
    && v.every((m) => m && typeof m === 'object' && !Array.isArray(m) && typeof m.role === 'string')
  );
}

/** Resolve a value to a transcript for the TOP-LEVEL view: a bare message array,
 *  or a single-key `{ messages | input | prompt | conversation | context: [...] }`
 *  envelope. The sole-key guard means a multi-field object (e.g. { context,
 *  userQuery, systemPrompt }) is NOT unwrapped here — it renders through JsonView
 *  so the sibling fields survive and the nested array still becomes titled turns. */
function asMessageArray(parsed) {
  if (isMessageArray(parsed)) return parsed;
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length === 1) {
    for (const k of ['messages', 'input', 'prompt', 'conversation', 'context']) {
      if (isMessageArray(parsed[k])) return parsed[k];
    }
  }
  return null;
}

/** Render a chat transcript as a stack of titled turns — the role is the title
 *  (System / User / Assistant / …) and the message body renders open below it,
 *  never wrapped in its own box. A lone `content` string renders as markdown
 *  directly; richer messages (tool_calls, name, …) render as an open field list. */
export function MessagesView({ messages, onRunClick, onSpanClick }) {
  const entityProps = { onRunClick, onSpanClick };
  return (
    <div className="flex min-w-0 flex-col gap-3">
      {messages.map((m, i) => {
        const title = ROLE_LABEL[String(m.role).toLowerCase()] || m.role;
        const rest = { ...m };
        delete rest.role;
        const keys = Object.keys(rest);
        const onlyContent = keys.length === 1 && keys[0] === 'content';
        // Copy the turn's content when that's all there is, else the whole turn.
        const copyValue = onlyContent ? rest.content : m;
        // Fold away very large turns by default; keep normal ones open.
        const size = stringifyForCopy(copyValue).length;
        return (
          <Collapsible
            key={i}
            title={title}
            copyValue={copyValue}
            preview={textPreview(copyValue)}
            defaultOpen={size <= 1200}
          >
            {keys.length === 0 ? (
              <span className="text-[color:var(--text-muted)]">—</span>
            ) : onlyContent ? (
              <JsonView value={rest.content} {...entityProps} />
            ) : (
              <JsonView value={rest} {...entityProps} />
            )}
          </Collapsible>
        );
      })}
    </div>
  );
}

/** View component: renders chat-style markdown for plain text, a titled turn
 *  stack for chat transcripts, and an open key/value outline for other JSON.
 *  No inline toggle — the tab-level Formatted/JSON switch decides whether
 *  RichValueView or JsonRawView is rendered. */
export function RichValueView({ value, onRunClick, onSpanClick }) {
  const c = classify(value);
  if (c.kind === 'empty') return <span className="text-[12px] text-[color:var(--text-muted)]">—</span>;
  if (c.kind === 'text') {
    return (
      <AgentMarkdownContent onRunClick={onRunClick} onSpanClick={onSpanClick}>
        {c.text}
      </AgentMarkdownContent>
    );
  }
  const messages = asMessageArray(c.parsed);
  if (messages) {
    return <MessagesView messages={messages} onRunClick={onRunClick} onSpanClick={onSpanClick} />;
  }
  return <JsonView value={c.parsed} onRunClick={onRunClick} onSpanClick={onSpanClick} />;
}

export function renderCell(key, row, callbacks = {}) {
  switch (key) {
    case 'run_id':
      return (
        <span className="inline-flex items-center gap-2 whitespace-nowrap" title={`${row.status || ''} ${row.run_id || ''}`.trim()}>
          <StatusDot status={row.status} />
          <span className="font-mono text-[11px] text-[color:var(--text-secondary)]">
            {truncate(row.run_id, 14)}
          </span>
          <AnnotationBadge
            runId={row.run_id}
            onClick={typeof callbacks.onAnnotationClick === 'function'
              ? (e) => { e.stopPropagation(); callbacks.onAnnotationClick(row); }
              : undefined}
          />
        </span>
      );
    case 'agent_name':
      return <span className="font-medium text-[color:var(--text-primary)]">{row.agent_name || '—'}</span>;
    case 'started_at':
      return (
        <span className="tabular-nums text-[color:var(--text-secondary)]" title={formatDate(row.started_at)}>
          {formatRelativeTime(row.started_at)}
        </span>
      );
    case 'duration_ms':
      return <span className="tabular-nums">{formatDuration(row.duration_ms)}</span>;
    case 'total_tokens_in':
      return <span className="tabular-nums">{formatNumber(row.total_tokens_in)}</span>;
    case 'total_tokens_out':
      return <span className="tabular-nums">{formatNumber(row.total_tokens_out)}</span>;
    case 'total_cost':
      return <span className="tabular-nums">{formatCost(row.total_cost)}</span>;
    case 'span_count':
      return <span className="tabular-nums">{formatNumber(row.span_count)}</span>;
    case 'tool_count':
      return <span className="tabular-nums">{formatNumber(row.tool_count)}</span>;
    case 'error_count':
      return <span className="tabular-nums">{formatNumber(row.error_count)}</span>;
    case 'feedback':
      return <FeedbackCell row={row} />;
    case 'cluster_id':
      return row.cluster_id
        ? <span className="rounded bg-[color:var(--surface-hover)] px-1.5 py-0.5 text-[11px] text-[color:var(--text-secondary)]">#{row.cluster_id}</span>
        : <span className="text-[color:var(--text-muted)]">—</span>;
    case 'distinct_id':
      return <DistinctIdLink value={row.distinct_id} />;
    case 'conversation_id':
      return <span className="font-mono text-[11px] text-[color:var(--text-secondary)]">{truncate(row.conversation_id)}</span>;
    case 'rage_pct':
      return <ScorePill value={row.rage_pct ?? row.rage_score} kind="rage" />;
    case 'sat_pct':
      return <ScorePill value={row.satisfaction_pct ?? row.satisfaction_score} kind="sat" />;
    default:
      return <span className="text-[color:var(--text-muted)]">—</span>;
  }
}
